<template>
  <div v-if="visible" class="photoLocationPicker">
    <div class="pickerHeader">
      <span class="pickerTitle">Marcar localização da foto</span>
      <button class="pickerClose" @click="$emit('close')">✕</button>
    </div>
    <div ref="map" class="pickerMap"></div>
    <div v-if="isLoading" class="pickerFeedback">
      <span>Carregando mapa...</span>
    </div>
    <div v-if="selectedLat && selectedLng" class="pickerCoords">
      {{ Number(selectedLat).toFixed(7) }}, {{ Number(selectedLng).toFixed(7) }}
    </div>
  </div>
</template>

<script>
const MAPS_SCRIPT_ID = "winfit-google-maps-sdk-photo-location";
const DEFAULT_CENTER = { lat: -14.235, lng: -51.9253 };

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);

  const existing = document.getElementById(MAPS_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.google.maps), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  const existingMain = document.getElementById("winfit-google-maps-sdk");
  if (existingMain) {
    return new Promise((resolve) => {
      const check = () => {
        if (window.google?.maps) resolve(window.google.maps);
        else setTimeout(check, 100);
      };
      check();
    });
  }

  const key = process.env.VUE_APP_KEY_API_GOOGLE_MAPS;
  if (!key) return Promise.reject(new Error("Chave do Google Maps não configurada."));

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Falha ao carregar Google Maps."));
    document.head.appendChild(script);
  });
}

export default {
  name: "PhotoLocationPicker",
  props: {
    visible: { type: Boolean, default: false },
    latitude: { type: [String, Number], default: null },
    longitude: { type: [String, Number], default: null },
  },
  data() {
    return {
      map: null,
      marker: null,
      mapClickListener: null,
      isLoading: false,
      selectedLat: this.latitude || null,
      selectedLng: this.longitude || null,
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.$nextTick(() => this.activateMap());
      } else {
        this.destroyMap();
      }
    },
  },
  methods: {
    destroyMap() {
      if (this.mapClickListener && window.google?.maps) {
        window.google.maps.event.removeListener(this.mapClickListener);
      }
      if (this.marker) this.marker.setMap(null);
      this.mapClickListener = null;
      this.marker = null;
      this.map = null;
    },
    async activateMap() {
      await this.$nextTick();
      await this.initializeMap();
    },
    async initializeMap() {
      if (!this.$refs.map || this.isLoading) return;
      this.isLoading = true;
      try {
        await loadGoogleMaps();
        if (!this.$refs.map || this._isBeingDestroyed) return;

        const saved = this.savedCoords();
        const geoloc = await this.getBrowserLocation();
        const center = saved || geoloc || DEFAULT_CENTER;
        const zoom = saved ? 17 : geoloc ? 15 : 4;

        this.map = new window.google.maps.Map(this.$refs.map, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        this.mapClickListener = this.map.addListener("click", ({ latLng }) => {
          const lat = latLng.lat().toFixed(7);
          const lng = latLng.lng().toFixed(7);
          this.selectedLat = lat;
          this.selectedLng = lng;
          this.setMarker({ lat: latLng.lat(), lng: latLng.lng() });
          this.$emit("location-selected", { latitude: lat, longitude: lng });
        });

        if (saved) this.setMarker(saved);
      } catch {
        // silent
      } finally {
        this.isLoading = false;
      }
    },
    savedCoords() {
      const lat = Number(this.latitude);
      const lng = Number(this.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    },
    getBrowserLocation() {
      return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 5000, maximumAge: 60000 }
        );
      });
    },
    setMarker(coords) {
      if (!this.map) return;
      if (!this.marker) {
        this.marker = new window.google.maps.Marker({ map: this.map, position: coords });
      } else {
        this.marker.setPosition(coords);
      }
      this.map.panTo(coords);
    },
  },
  beforeDestroy() {
    this.destroyMap();
  },
};
</script>

<style scoped>
.photoLocationPicker {
  margin-top: 8px;
  border: 1px solid #e2e8e6;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}
.pickerHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #f7faf9;
  border-bottom: 1px solid #e2e8e6;
}
.pickerTitle {
  font-size: 11px;
  font-weight: 600;
  color: #006256;
}
.pickerClose {
  background: none;
  border: none;
  font-size: 14px;
  color: #6c757d;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}
.pickerClose:hover { color: #a01818; }
.pickerMap {
  width: 100%;
  height: 220px;
  background: #eef2f1;
}
.pickerFeedback {
  padding: 8px 10px;
  font-size: 11px;
  color: #6c757d;
  text-align: center;
}
.pickerCoords {
  padding: 4px 10px;
  font-size: 10px;
  color: #006256;
  background: #f0f7f5;
  text-align: center;
  font-family: monospace;
}
</style>
